/* eslint-disable */
import { useShallow } from 'zustand/react/shallow';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	LineElement,
	PointElement,
	Filler,
} from 'chart.js';
import { connect } from 'react-redux';
import { analyticsAction, formActions } from '@/Actions';
import { useQualityAnalysisStore } from './QualityAnalysisStore';
import HeaderSection from './HeaderSection';
import SummaryCards from './SummaryCards';
import { useEffect, useState, useMemo } from 'react';
import { initializeStateChangeListeners, syncFromUrl } from './Services/stateChangeService';
import { BackdropLoader } from '../Loaders';
import { Bar } from 'react-chartjs-2';
import StatisticalChart from './StatisticalAnalysis/StatisticalChart';
import { Button } from '@/Components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
	LineElement,
	PointElement,
	Filler
);

function QualityAnalysisPage({
	date = new Date().toLocaleDateString('en-US', {
		month: 'long',
		day: 'numeric',
		year: 'numeric',
	}),
	fetchBrands,
	products,
	fetchProducts,
}) {
	const {
		activeAnalysisTab,
		activeConfigType,
		isLoading,
		qualityAnalysisChartData,
		activeTimeRangeDropdown,
		activeProductWiseDropdown,
		activeGeographyWiseDropdown,
		setGlobalProductList,
	} = useQualityAnalysisStore(
		useShallow((state) => ({
			activeAnalysisTab: state.activeAnalysisTab,
			isLoading: state.loading,
			activeConfigType: state.activeConfigType,
			qualityAnalysisChartData: state.qualityAnalysisChartData,
			activeTimeRangeDropdown: state.activeTimeRangeDropdown,
			activeProductWiseDropdown: state.activeProductWiseDropdown,
			setGlobalProductList: state.setGlobalProductList,
			activeGeographyWiseDropdown: state.activeGeographyWiseDropdown,
		}))
	);

	const [currentPage, setCurrentPage] = useState(0);
	// Use PageSize from chartInfo if available, otherwise default to 31
	const itemsPerPage = useMemo(() => {
		return qualityAnalysisChartData?.chartInfo?.pageSize || 31;
	}, [qualityAnalysisChartData?.chartInfo?.pageSize]);

	useEffect(() => {
		setCurrentPage(0);
	}, [qualityAnalysisChartData]);

	const paginatedChartData = useMemo(() => {
		if (!qualityAnalysisChartData?.chartData?.labels?.length)
			return qualityAnalysisChartData;

		const originalData = qualityAnalysisChartData.chartData;

		// If data is small enough, return as is
		if (originalData.labels.length <= itemsPerPage) {
			return qualityAnalysisChartData;
		}

		// Calculate pagination
		const startIndex = currentPage * itemsPerPage;
		const endIndex = Math.min(
			startIndex + itemsPerPage,
			originalData.labels.length
		);

		// Create paginated version
		const paginatedData = {
			...qualityAnalysisChartData,
			chartData: {
				labels: originalData.labels.slice(startIndex, endIndex),
				labelIds:
					originalData.labelIds?.slice(startIndex, endIndex) || [],
				// Use the 'dataset' property from the original data
				dataset: Array.isArray(originalData.dataset)
					? originalData.dataset.map((dataset) => ({
						...dataset,
						data:
							dataset.data?.slice(startIndex, endIndex) || [],
					}))
					: [],
			},
		};

		return paginatedData;
	}, [qualityAnalysisChartData, currentPage, itemsPerPage]);

	useEffect(() => {
		fetchBrands({
			entity: 'BRANDS',
			req: {
				params: {
					id: '',
				},
			},
			method: 'LIST',
		});
		fetchProducts({
			entity: 'ALL_PRODUCTS',
			req: {
				params: {
					id: '',
					variants: true,
					skuType: '',
					combo: true,
				},
			},
			method: 'LIST',
			showNotification: true,
		});
	}, []);

	useEffect(() => {
		const unSubs = initializeStateChangeListeners();
		return unSubs;
	}, []);

	useEffect(() => {
		syncFromUrl();
	}, []);

	useEffect(() => {
		setGlobalProductList(products);
	}, [products]);

	// Add a useEffect to reset pagination when chart data changes
	useEffect(() => {
		setCurrentPage(0);
	}, [qualityAnalysisChartData?.chartData?.labels]);

	function buildXAxisLabel(
		configType,
		timeDropdown,
		productDropdown,
		geographyDropdown
	) {
		if (configType === 'time') {
			switch (timeDropdown) {
				case 'year':
					return 'Months in Year';
				case 'quarter':
					return 'Weeks in Quarter';
				case 'month':
					return 'Days in Month';
				default:
					return 'Time Period';
			}
		} else if (configType === 'product') {
			switch (productDropdown) {
				case 'brand':
					return 'Categories';
				case 'category':
					return 'Sub-Categories';
				case 'subCategory':
					return 'Products';
				default:
					return 'Product';
			}
		} else if (configType === 'geography') {
			switch (geographyDropdown) {
				case 'brand':
					return 'Regions';
				case 'region':
					return 'Cities';
				case 'city':
					return 'Stores';
				default:
					return 'Geography';
			}
		}
		return 'Data Points';
	}

	const renderContent = () => {
		if (
			activeAnalysisTab === 'quality' ||
			activeAnalysisTab === 'failure'
		) {
			return (
				<>
					<HeaderSection />
					<div className='px-2 flex flex-col flex-1 overflow-hidden'>
						<div className='px-6'>
							<SummaryCards />
						</div>
						<div className='rounded-lg flex-1 flex flex-col justify-between border border-gray-200 overflow-hidden'>
							<div className='flex-1 flex flex-col overflow-hidden relative'>
								{qualityAnalysisChartData?.chartData?.labels
									?.length > itemsPerPage && (
										<div className='flex  justify-between w-1/4 absolute top-1 right-1 rounded-lg items-center py-3 px-2  border border-yellow-400 bg-gray-50'>
											{currentPage > 0 ? (
												<Button
													variant='outline'
													size='sm'
													onClick={() =>
														setCurrentPage((prev) =>
															Math.max(0, prev - 1)
														)
													}
													className='flex flex-1 items-center gap-1 bg-white shadow-sm'
												>
													<ChevronLeft size={16} />{' '}
													Previous
												</Button>
											) : (
												<div className='flex-1'></div> // Empty space placeholder when button is hidden
											)}

											<span className='text-xs flex-1 text-center font-medium text-gray-700'>
												Page {currentPage + 1} of{' '}
												{Math.ceil(
													qualityAnalysisChartData
														.chartData.labels.length /
													itemsPerPage
												)}
											</span>

											{currentPage <
												Math.ceil(
													qualityAnalysisChartData.chartData
														.labels.length / itemsPerPage
												) -
												1 ? (
												<Button
													variant='outline'
													size='sm'
													onClick={() =>
														setCurrentPage((prev) =>
															Math.min(
																Math.ceil(
																	qualityAnalysisChartData
																		.chartData
																		.labels
																		.length /
																	itemsPerPage
																) - 1,
																prev + 1
															)
														)
													}
													className='flex flex-1 items-center gap-1 bg-white shadow-sm'
												>
													Next <ChevronRight size={16} />
												</Button>
											) : (
												<div className='flex-1'></div> // Empty space placeholder when button is hidden
											)}
										</div>
									)}
								<div className='w-full flex-1 overflow-hidden'>
									<Bar
										data={{
											labels: paginatedChartData
												?.chartData?.labels,
											datasets:
												paginatedChartData?.chartData
													?.datasets ||
												paginatedChartData?.chartData
													?.dataset ||
												[],
										}}
										options={{
											responsive: true,
											maintainAspectRatio: false,
											scales: {
												x: {
													stacked: true,
													grid: {
														display: false,
													},
													title: {
														display: true,
														text: qualityAnalysisChartData?.chartInfo?.xAxesLabel || buildXAxisLabel(
															activeConfigType,
															activeTimeRangeDropdown,
															activeProductWiseDropdown,
															activeGeographyWiseDropdown
														),
														font: {
															size: 12,
															weight: 'bold',
														},
													},
												},
												y: {
													stacked: true,
													grid: {
														display: true,
													},
													title: {
														display: true,
														text: qualityAnalysisChartData?.chartInfo?.yAxisLabel || 'Batch Count',
														font: {
															size: 12,
															weight: 'bold',
														},
													},
												},
											},
											plugins: {
												datalabels: {
													align: 'start',
													padding: '0',
													anchor: 'center',
													maxRotation: 0,
													rotation: 270,
													// formatter: dataLabelFormatter,
													// display: showDataLabels,
													clamp: false,
													color: '#000',
													font: {
														weight: '400',
														size: 9,
													},
												},
												title: {
													display: true,
													text: qualityAnalysisChartData?.chartInfo?.chartTitle || 'Food Quality Performance',
													font: {
														color: 'black',
														size: 16,
													},
												},
											},
										}}
									/>
								</div>
								{/* Add pagination controls AFTER the chart with better visibility */}
							</div>
						</div>
					</div>
				</>
			);
		} else if (activeAnalysisTab === 'statistical') {
			return (
				<>
					<HeaderSection hideFilters={true} />
					<StatisticalChart />
				</>
			);
		}
	};

	return (
		<div className=' h-[calc(100vh-64px)] flex flex-col p-0.5'>
			<BackdropLoader showProgress isLoading={isLoading} />
			{renderContent()}
		</div>
	);
}

const mapStateToProps = (state) => {
	const { LIST: brandsList } = state?.form?.BRANDS?.data ?? {};
	const products = state.form.ALL_PRODUCTS.data.LIST;
	const {
		STATISTICAL_FILTER,
		DATE_FILTER_TODAY_DATE,
		DATE_RANGE_FILTER,
		GEO_FILTER,
		BRAND_FILTER,
		STORE_FILTER,
		FAILURE_ANALYSIS_FILTER,
		PRODUCT_FILTER,
	} = state?.analytics;
	return {
		brandsList,
		DATE_FILTER_TODAY_DATE,
		DATE_RANGE_FILTER,
		GEO_FILTER,
		BRAND_FILTER,
		STORE_FILTER,
		FAILURE_ANALYSIS_FILTER,
		PRODUCT_FILTER,
		STATISTICAL_FILTER,
		products,
	};
};

export default connect(mapStateToProps, {
	setDateFilter: analyticsAction.setDateFilterTodayDate,
	setDateRangeFilter: analyticsAction.setDateRangeFilter,
	applyFilters: analyticsAction.applyFilters,
	fetchBrands: formActions.gateway,
	fetchProducts: formActions.gateway,
})(QualityAnalysisPage);
